import React from 'react';
import s from './ActionButton.scss';
import classnames from 'classnames';
import {isFunction} from 'util';
import {IProvidedGlobalProps, withGlobalProps} from '../../providers/GlobalPropsProvider';
import {getProductWidgetSettingsFromProps, WidgetActionEnum} from '../../commons/styleParamsService';
import {
  IProvidedTranslationProps,
  withTranslations,
} from '@wix/wixstores-client-common-components/dist/src/outOfIframes/translations';

export enum DataHook {
  Root = 'action-button',
}

export interface IActionButtonProps extends IProvidedGlobalProps, IProvidedTranslationProps {
  onFocusTriggered?: Function;
  isFocused?: boolean;
  onSubmit?: Function;
  showOnlyOnHover?: boolean;
  topGutter?: boolean;
  isCentered?: boolean;
}

@withGlobalProps
@withTranslations('globals.texts')
export class ActionButton extends React.Component<IActionButtonProps> {
  public static defaultProps: Partial<IActionButtonProps> = {
    topGutter: false,
    isCentered: false,
    showOnlyOnHover: false,
  };

  private readonly actionButtonRef: React.RefObject<HTMLButtonElement> = React.createRef();

  public componentDidMount() {
    if (this.props.isFocused) {
      this.focusActionButton();
    }
  }

  public componentDidUpdate(prevProps: IActionButtonProps) {
    if (this.props.isFocused !== prevProps.isFocused) {
      this.focusActionButton();
    }
  }

  private readonly focusActionButton = () => {
    const {onFocusTriggered} = this.props;
    this.actionButtonRef.current.focus();
    if (isFunction(onFocusTriggered)) {
      onFocusTriggered();
    }
  };

  private readonly handleSubmit = (e: React.MouseEvent<HTMLElement>) => {
    const {onSubmit} = this.props;
    e.preventDefault();

    if (isFunction(onSubmit)) {
      e.stopPropagation();
      onSubmit();
    }
  };

  public render() {
    const {t, topGutter, isCentered, showOnlyOnHover} = this.props;

    const settings = getProductWidgetSettingsFromProps(this.props);
    const text = t(this.getTranslationKeyByAction(settings.widgetAction));

    const buttonClasses = classnames(s.actionButton, {
      [s.actionButtonShowOnlyOnHover]: showOnlyOnHover,
      [s.disabled]: this.isDisabled,
      [s.topGutter]: topGutter,
      [s.centered]: isCentered,
    });

    return (
      <button
        className={classnames(buttonClasses)}
        data-hook={DataHook.Root}
        disabled={this.isDisabled}
        ref={this.actionButtonRef}
        type="submit"
        onClick={this.handleSubmit}>
        {text}
      </button>
    );
  }

  private get isDisabled(): boolean {
    const {
      globals: {product},
    } = this.props;
    return product.price === 0;
  }

  private getTranslationKeyByAction(widgetAction: string): string {
    if (widgetAction === WidgetActionEnum.NAVIGATE) {
      return 'NAVIGATE_TO_PRODUCT_PAGE_BUTTON';
    } else if (widgetAction === WidgetActionEnum.ADDTOCART && this.isDisabled) {
      return 'ADD_TO_CART_BUTTON_ZERO_PRICE';
    }
    return 'ADD_TO_CART_BUTTON';
  }
}
